import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import type {
  Position,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import { blockItems } from './report-flow-reducer';
import type { ReportFlowPage } from './report-flow-types';

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const standalonePosition = (
  item: PositionedItem,
  y: number,
  gridColumns: number
): Position => {
  const width = clamp(Math.round(item.position.width), 1, gridColumns);
  const free = gridColumns - width;
  const offsetByAlign = { left: 0, center: Math.floor(free / 2), right: free };
  const x = offsetByAlign[item.align ?? 'left'];
  return { x, y, width, height: item.position.height };
};

const columnPositions = (
  items: PositionedItem[],
  y: number,
  gridColumns: number
): Position[] => {
  const totalFr = items.reduce(
    (sum, item) => sum + Math.max(item.position.width, 1),
    0
  );
  let x = 0;
  return items.map((item, index) => {
    const isLast = index === items.length - 1;
    const widthLeft = gridColumns - x - (items.length - 1 - index);
    const width = isLast
      ? gridColumns - x
      : clamp(
          Math.round(
            (Math.max(item.position.width, 1) / totalFr) * gridColumns
          ),
          1,
          widthLeft
        );
    const position = { x, y, width, height: item.position.height };
    x += width;
    return position;
  });
};

export const layoutReportFlowForExport = (
  pages: ReportFlowPage[],
  {
    pageGridRows = DEFAULT_GRID_ROWS,
    gridColumns = DEFAULT_GRID_COLUMNS,
  }: { pageGridRows?: number; gridColumns?: number } = {}
): { items: PositionedItem[]; gridRows: number } => {
  const laidOut: PositionedItem[] = [];

  let band = 1;
  for (const page of pages) {
    let cursor = 0;
    let bandUsed = false;
    for (const block of page.blocks) {
      const placed = blockItems(block).map((item) => ({
        ...item,
        position: {
          ...item.position,
          height: clamp(Math.round(item.position.height), 1, pageGridRows),
        },
      }));
      const [first] = placed;
      if (!first) continue;

      const blockHeight = Math.max(
        ...placed.map((item) => item.position.height)
      );
      if (cursor > 0 && cursor + blockHeight > pageGridRows) {
        band += 1;
        cursor = 0;
      }
      const y = band * pageGridRows + cursor;
      const positions =
        placed.length === 1
          ? [standalonePosition(first, y, gridColumns)]
          : columnPositions(placed, y, gridColumns);
      placed.forEach((item, index) => {
        const position = positions[index];
        if (position) laidOut.push({ ...item, position });
      });
      cursor += blockHeight;
      bandUsed = true;
    }
    if (bandUsed) band += 1;
  }

  return { items: laidOut, gridRows: (band + 1) * pageGridRows };
};
